"use client"
import {
  createContext,
  useContext,
  type CSSProperties,
  type ReactNode,
} from "react"

// The shared opener for every HUD hover detail ("Distribution of Power",
// "Data Centers", "Median Income", the policy / labor / capability detail
// titles): one mono small-caps title tier with a sans description tier below
// it, so every hover menu reads as one system. Reuse happens through these
// components — not shared style consts or CSS vars — so the type treatment
// is visible right here and can't drift per menu.
// The title/description TYPE is tab-switchable (the 2026-07-11 hierarchy
// exploration): each treatment is one className string per tier, so the faces
// and sizes are editable right here. Sizes reference the HUD's existing
// voices — 13px = the stock opener, 11px = the takeover region headers
// (POWER_HOVER_STYLE.headerFontSize), 9.5px = the breakdown's body rows
// (POWER_BREAKDOWN_HIERARCHY_STYLE), 8.5px = the stat-row labels.
export type HudHoverHeaderTreatmentId =
  | "stock" // 13px mono caps title / 13px sans description
  | "smaller-type" // both tiers down to 11px — same structure, HUD scale
  | "mono-subtitle" // stock title; description goes mono 9.5px (body-row voice)
  | "column-header" // title as the region-header idiom: 11px bold mono, black underline
  | "micro-label" // title whispers at the 8.5px stat-label tier; description leads
  | "all-mono-small" // one face one size: everything mono 10px
  | "serif-subtitle" // 11px caps title; the essay's et-book serif as subtitle
  | "light-caps" // normal-weight 11px caps — wide tracking, not bold, carries the label
  | "red-tag" // title as the LABOR MIX titleTag voice: 8.5px red caps
  | "wide-banner" // 9px caps at 0.25em tracking — an architectural micro-banner
  | "value-voice" // the stat-row pair: 13px bold value-style title / 8.5px label sub

export const HUD_HOVER_HEADER_TREATMENTS: Record<
  HudHoverHeaderTreatmentId,
  { title: string; description: string }
> = {
  stock: {
    title: "font-mono font-bold uppercase text-[13px] tracking-[0.06em]",
    description: "font-sans mt-0.5 text-[13px] leading-[1.45]",
  },
  "smaller-type": {
    title: "font-mono font-bold uppercase text-[11px] tracking-[0.06em]",
    description: "font-sans mt-0.5 text-[11px] leading-[1.45]",
  },
  "mono-subtitle": {
    title: "font-mono font-bold uppercase text-[13px] tracking-[0.06em]",
    description: "font-mono mt-1 text-[9.5px] leading-[1.5]",
  },
  "column-header": {
    title: "font-mono font-bold text-[11px] pb-[3px] border-b border-black",
    description: "font-mono mt-[6px] text-[9.5px] leading-[1.5]",
  },
  "micro-label": {
    title: "font-mono font-bold uppercase text-[8.5px] tracking-[0.08em]",
    description: "font-sans mt-1 text-[13px] leading-[1.45]",
  },
  "all-mono-small": {
    title: "font-mono font-bold uppercase text-[10px] tracking-[0.06em]",
    description: "font-mono mt-1 text-[10px] leading-[1.5]",
  },
  "serif-subtitle": {
    title: "font-mono font-bold uppercase text-[11px] tracking-[0.06em]",
    description: "font-et-book italic mt-1 text-[13px] leading-[1.4]",
  },
  "light-caps": {
    title: "font-mono uppercase text-[11px] tracking-[0.14em]",
    description: "font-sans mt-0.5 text-[11px] leading-[1.45]",
  },
  "red-tag": {
    title:
      "font-mono font-bold uppercase text-[8.5px] tracking-[0.08em] text-[var(--accent)]",
    description: "font-mono mt-1 text-[9.5px] leading-[1.5]",
  },
  "wide-banner": {
    title: "font-mono font-bold uppercase text-[9px] tracking-[0.25em]",
    description: "font-sans mt-1 text-[11px] leading-[1.45]",
  },
  "value-voice": {
    title: "font-mono font-bold text-[13px]",
    description: "font-mono mt-0.5 text-[8.5px] leading-[1.5]",
  },
}

// Which header treatment this tab uses — provided per HUD variant in
// variants.tsx (BelowDetailHudSwapProviders); default keeps production stock.
export const HudHoverHeaderTreatmentContext =
  createContext<HudHoverHeaderTreatmentId>("stock")

export const HudHoverTitle = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const treatment = HUD_HOVER_HEADER_TREATMENTS[useContext(HudHoverHeaderTreatmentContext)]
  return <div className={`${treatment.title} ${className ?? ""}`}>{children}</div>
}

// The rule that sets a hover detail off from the canvas above it (the
// labor section's "rule-above" vocabulary, promoted to every hover detail:
// the stuff below this line is additional info). The rule always gets 18px
// of air on each side — pass the positioning as className:
// - hosts whose detail top edge is PINNED (the below-HUD slot, where the
//   detail must align to the labor strip's footprint) hang it above with
//   `absolute inset-x-0 -top-[18px]` inside a relative wrapper;
// - free-flowing hosts use HudHoverTakeoverDetail below, which spaces it
//   in flow.
// Render it INSIDE the detail's fade so it rides the fade and the
// item-to-item handoffs.
//
// The rule's LOOK is tab-switchable (the 2026-07-11 subtlety exploration):
// each treatment is one heightPx + one CSS background string painting the
// line — dots and stubs are gradients, so every look is editable right here.
// The labor section's "rule-above" hover rule reads the same treatment
// (laborHoverSwap.tsx), so both separator systems change together per tab.
export type HudHoverRuleTreatmentId =
  | "stock" // the production 2px solid red rule
  | "black-dotted" // 1px black dots (1px on / 3px off)
  | "black-hairline" // 1px solid black
  | "black-stub" // short 28px solid black stub at the left edge
  | "red-dotted" // 1px RED dots — the dither field's vocabulary at hairline weight
  | "edge-ticks" // two 14px black ticks at the ends, nothing between

export const HUD_HOVER_RULE_TREATMENTS: Record<
  HudHoverRuleTreatmentId,
  { heightPx: number; background: string }
> = {
  stock: { heightPx: 2, background: "var(--accent)" },
  "black-dotted": {
    heightPx: 1,
    background: "repeating-linear-gradient(to right, #000 0 1px, transparent 1px 4px)",
  },
  "black-hairline": { heightPx: 1, background: "#000" },
  "black-stub": {
    heightPx: 1,
    background: "linear-gradient(#000, #000) left top / 28px 1px no-repeat",
  },
  "red-dotted": {
    heightPx: 1,
    background:
      "repeating-linear-gradient(to right, var(--accent) 0 1px, transparent 1px 4px)",
  },
  "edge-ticks": {
    heightPx: 1,
    background:
      "linear-gradient(#000, #000) left top / 14px 1px no-repeat, linear-gradient(#000, #000) right top / 14px 1px no-repeat",
  },
}

// Which treatment this tab uses — provided once per HUD variant in
// variants.tsx (BelowDetailHudSwapProviders); default keeps production stock.
export const HudHoverRuleTreatmentContext =
  createContext<HudHoverRuleTreatmentId>("stock")

// Non-stock geometry (Joanna 2026-07-11): the detail content starts exactly
// where the HUD content it covers started, and the rule hangs ABOVE it,
// centered in the gap to the content above (the below-HUD slot's gap is the
// labor strip's mt-6 = 24px; -12 splits the boxes evenly, and the extra 2px
// down is Joanna's visual-centering nudge — the stats text above hangs below
// its box, so true box-center reads high). Every non-stock host (below-HUD
// slot, labor rule-above) hangs the rule at this same offset.
export const HUD_HOVER_RULE_CENTERED_TOP_PX = -10

// The bare takeovers (Data Centers, the capability ladder — non-stock
// treatments only) sit this many px below the lower half's top edge:
// perfectly flush read too tight against the graph above (Joanna 2026-07-11).
export const TAKEOVER_DETAIL_TOP_NUDGE_PX = 5

export const HudHoverDetailRule = ({
  className,
  style,
}: {
  className?: string
  // Positioning only (the hung-rule hosts' top offset) — the line's look
  // comes from the treatment.
  style?: CSSProperties
}) => {
  const treatment = HUD_HOVER_RULE_TREATMENTS[useContext(HudHoverRuleTreatmentContext)]
  return (
    <span
      aria-hidden
      className={`pointer-events-none block ${className ?? ""}`}
      style={{ height: treatment.heightPx, background: treatment.background, ...style }}
    />
  )
}

// The lower-half takeovers' detail body — STOCK ONLY (in the non-stock
// treatments both takeovers render bare, no rule, flush at the lower half's
// top edge; see the stockRule gates in capabilityHoverSwap.tsx and
// globeHoverSwap.tsx). The shared rule in flow — 18px below whatever the
// graph leaves above (its date guide label, "2030"), 18px above the detail —
// matching the air around the below-HUD slot's rule.
export const HudHoverTakeoverDetail = ({ children }: { children: ReactNode }) => (
  <div>
    <HudHoverDetailRule className="my-[18px]" />
    {children}
  </div>
)

// A hover detail's data section (region columns, ladder rows, datacenter
// list, the metric's value line) below the title/description opener — set
// off by the same 18px of air that separates the red rule from the title,
// so every hover menu breathes on one rhythm.
export const HudHoverDetailBody = ({
  className,
  style,
  children,
}: {
  className?: string
  // For genuinely dynamic values only (e.g. a rowGap picked from runtime
  // data) — static styling belongs in className.
  style?: CSSProperties
  children: ReactNode
}) => (
  <div className={`mt-[18px] ${className ?? ""}`} style={style}>
    {children}
  </div>
)

// Whether this tab drops the "Click for details" affordance from the hover
// details entirely (Joanna 2026-07-11, tab 6) — provided per HUD variant in
// variants.tsx (hoverClickHintDisabled).
export const HudHoverClickHintDisabledContext = createContext(false)

// The small "click opens the full panel" affordance at the bottom of the
// hover details whose HUD trigger opens a details panel (the stat-row
// metrics, the power treemap, the capability line). HudHoverDetailBody
// gives it the shared 18px of air above — the same gap as under the rule.
export const HudHoverClickHint = () => {
  const disabled = useContext(HudHoverClickHintDisabledContext)
  if (disabled) return null
  return (
    <HudHoverDetailBody className="font-mono text-[8.5px] text-[var(--accent)]">
      Click for details
    </HudHoverDetailBody>
  )
}

// Full black (no opacity dim) — palette per design-style-guide.md: the face
// change (sans or smaller mono, per treatment) is what de-emphasizes the
// description against the title.
export const HudHoverDescription = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const treatment = HUD_HOVER_HEADER_TREATMENTS[useContext(HudHoverHeaderTreatmentContext)]
  return <div className={`${treatment.description} ${className ?? ""}`}>{children}</div>
}
